import i18n from 'modules/core/lib/i18n'

import { EntityResolution } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'

/**
 * Formats a list of items with localized "or" conjunction.
 * e.g. ["a", "b", "c"] => "a, b or c"
 */
function formatListWithOr(items: string[]): string {
  if (items.length === 0) return ''
  if (items.length === 1) return items[0] ?? ''
  if (items.length === 2) return `${items[0]} ${i18n.t('common.or')} ${items[1]}`

  const allButLast = items.slice(0, -1).join(', ')
  const last = items[items.length - 1]
  return `${allButLast} ${i18n.t('common.or')} ${last}`
}

/**
 * Formats people group specs for display, resolving team IDs to names.
 */
export function formatPeopleGroupDisplayValue(
  value: string,
  entityResolution: EntityResolution
): string {
  try {
    const specs = JSON.parse(value)
    if (!Array.isArray(specs) || specs.length === 0) return value

    const teamMap = entityResolution['team'] ?? {}
    const parts: string[] = []

    for (const spec of specs) {
      if (spec.criterias) {
        const teamId = String(spec.criterias.input?.team_id ?? '')
        const teamName =
          teamMap[teamId] ??
          i18n.t('procurement.people_group_specs_editor.team_fallback', { id: teamId })
        if (spec.criterias.criteria === 'anyone_in_team') {
          parts.push(
            i18n.t('procurement.people_group_specs_editor.team_members_of', { team_name: teamName })
          )
        } else if (spec.criterias.criteria === 'leads_in_team') {
          parts.push(
            i18n.t('procurement.people_group_specs_editor.team_leads_of', { team_name: teamName })
          )
        }
      } else if (spec.whitelist) {
        const count = spec.whitelist.length
        parts.push(
          i18n.t('procurement.people_group_specs_editor.specific_employees_count', { count })
        )
      }
    }

    return formatListWithOr(parts) || value
  } catch {
    return value
  }
}

/**
 * Extracts entity IDs from people group specs for resolution.
 * Returns team IDs that need to be resolved to names.
 */
export function extractPeopleGroupEntityIds(value: string): Record<string, string[]> {
  const result: Record<string, string[]> = {}

  try {
    const specs = JSON.parse(value)
    if (Array.isArray(specs)) {
      for (const spec of specs) {
        if (spec.criterias?.input?.team_id) {
          if (!result['team']) {
            result['team'] = []
          }
          result['team'].push(String(spec.criterias.input.team_id))
        }
      }
    }
  } catch {
    // Not valid JSON, skip
  }

  return result
}
